// src/pages/NewCheckoutLink.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createCheckoutLink,
  getCheckoutLinks,
  updateCheckoutLink,
} from "../services/checkoutLinks";
import { toast } from "react-toastify";
import { Copy, Link2, ArrowLeft, Check } from "lucide-react";
import axios from "axios";
import { getAuthHeaders } from "../services/authHeaders";
import { PLATFORM_CHECKOUT_PARAMS } from "../utils/platformCheckoutTemplates";

const PLATFORMS_ENDPOINT = "https://api.sobevenda.com.br/api-proxy/affiliate/platforms";

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none";
const labelCls = "block text-xs mb-1 text-slate-600 dark:text-slate-300 font-medium";

async function fetchLinkById(id) {
  const token = localStorage.getItem("sobevenda_token");
  const res = await fetch(`https://api.sobevenda.com.br/api-proxy/affiliate/checkout-links/${id}`, {
    method: "GET",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Erro ao buscar link de checkout pelo ID");
  return await res.json();
}

export default function NewCheckoutLink() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [platformList, setPlatformList] = useState([]);
  const [platform, setPlatform] = useState("");
  const [name, setName] = useState("");
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [loadingPlatforms, setLoadingPlatforms] = useState(true);
  const [baseUrl, setBaseUrl] = useState("");

  useEffect(() => {
    async function fetchPlatforms() {
      try {
        const { data } = await axios.get(PLATFORMS_ENDPOINT, { headers: getAuthHeaders() });
        setPlatformList(data.filter((p) => PLATFORM_CHECKOUT_PARAMS[p.name]));
      } catch { toast.error("Erro ao carregar plataformas"); }
      finally { setLoadingPlatforms(false); }
    }
    fetchPlatforms();
  }, []);

  useEffect(() => { setGeneratedUrl(""); setBaseUrl(""); }, [platform]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const all = await getCheckoutLinks();
        const link = all.find((l) => String(l.id) === id);
        if (!link) return navigate("/checkout-links");
        setPlatform(link.platform);
        setName(link.name);
        setBaseUrl(link.checkout_link?.split("?")[0] || "");
        setGeneratedUrl(link.checkout_link || "");
      } catch { toast.error("Não foi possível carregar"); navigate("/checkout-links"); }
    })();
  }, [isEdit, id, navigate]);

  function generateCheckoutUrl(base, platform, params) {
    if (!base) return "";
    if (!params) params = (PLATFORM_CHECKOUT_PARAMS[platform] || []).join("&");
    const full = params ? `${base}?${params}` : base;
    if (platform?.toLowerCase() === "buygoods") return `<script src="${full}"></script>`;
    return full;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!platform) return toast.error("Selecione a plataforma");
    try {
      const params = (PLATFORM_CHECKOUT_PARAMS[platform] || []).join("&");
      let linkData;
      if (isEdit) {
        await updateCheckoutLink(id, { platform, name, params });
        const all = await getCheckoutLinks();
        linkData = all.find((l) => String(l.id) === id);
        if (!linkData) throw new Error("Link não encontrado após salvar.");
        const urlBase = linkData.checkout_link?.split("?")[0] || linkData.checkout_link || "";
        setBaseUrl(urlBase);
        setGeneratedUrl(generateCheckoutUrl(urlBase, platform, linkData.params));
      } else {
        linkData = await createCheckoutLink({ platform, name, params });
        if (!linkData?.checkout_link) throw new Error("Link não encontrado após salvar.");
        const urlBase = linkData.checkout_link.split("?")[0];
        setBaseUrl(urlBase);
        setGeneratedUrl(generateCheckoutUrl(urlBase, platform, params));
      }
      toast.success(isEdit ? "Atualizado!" : "Criado!");
    } catch (err) { toast.error(err.message || "Erro ao salvar"); }
  };

  const handleCopy = () => {
    if (!generatedUrl) return;
    navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
    navigate("/checkout-links");
  };

  const isBuygoods = platform?.toLowerCase() === "buygoods";
  const resultLabel = isBuygoods ? "Snippet (cole no site)" : "URL Final";

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-xl space-y-6">

        {/* HEADER */}
        <div>
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 mb-4 transition">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Link2 className="w-6 h-6" />
            {isEdit ? "Editar Link de Checkout" : "Novo Link de Checkout"}
          </h1>
        </div>

        {/* CARD */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
          {!generatedUrl ? (
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Plataforma */}
              <div>
                <label className={labelCls} htmlFor="platform">Plataforma <span className="text-red-500">*</span></label>
                <select id="platform" value={platform} onChange={(e) => setPlatform(e.target.value)} required disabled={isEdit} className={`${inputCls} disabled:opacity-60`}>
                  <option value="">Selecione a plataforma</option>
                  {platformList.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
              </div>

              {/* Nome */}
              <div>
                <label className={labelCls} htmlFor="name">Nome do projeto / oferta</label>
                <input id="name" type="text" placeholder="ex: oferta-azul-buygoods" value={name}
                  onChange={(e) => setName(e.target.value)} className={inputCls} />
              </div>

              {/* Botão */}
              <button type="submit" disabled={loadingPlatforms}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
                <Link2 className="w-4 h-4" />
                {isEdit ? "Salvar alterações" : "Gerar Link"}
              </button>
            </form>
          ) : (
            /* URL / Snippet gerado */
            <div className="space-y-3">
              <p className="text-sm font-bold text-slate-900 dark:text-slate-100">{resultLabel}</p>
              <div className="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-4 py-3">
                {isBuygoods ? (
                  <pre className="font-mono text-xs text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-all flex-1 select-all">
                    {generatedUrl}
                  </pre>
                ) : (
                  <span className="font-mono text-xs text-slate-700 dark:text-slate-300 break-all flex-1 select-all">
                    {generatedUrl}
                  </span>
                )}
                <button type="button" onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 shrink-0">
                  {copied ? <><Check className="w-3.5 h-3.5" /> Copiado!</> : <><Copy className="w-3.5 h-3.5" /> Copiar</>}
                </button>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {isBuygoods
                  ? "Cole este snippet no seu site/página para rastrear os checkouts."
                  : "Use esta URL como postback de checkout na plataforma selecionada."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}